import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSData, RequestError, Service } from 'webapp-types';
import { copyObject, utf8_to_b64 } from 'webapp/utils/common';
import { getDateRangeIdFromFormula } from 'webapp/utils/date-range';

import didUpdate from 'webapp/modifiers/did-update';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonGenieButtonComponent from 'webapp/components/common/genie/button';
import DataStatusIconComponent from 'webapp/components/data-status-icon';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	chartState: MetricInsightsChartState;
	dateRange: DateRange | null;
	model: MetricSetup;
	forceLoadingState: boolean;
	origin: string;
}

export default class MetricDetailsExploreRelatedMetricsPreviousPeriodComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('connect') declare connectService: Service.Connect;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;

	private paramHash = '';
	@tracked isLoading = false;
	@tracked dataSelect: DataSelect | null = null;

	private dataQueryXHR: JQuery.jqXHR<unknown> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.recreateDataQuery();
	}

	get metricSetup() {
		return this.args.model;
	}

	get metric() {
		return this.metricSetup.metric!; // already filtered in parent component
	}

	get isConnected() {
		return this.metric.isOnSource;
	}

	get source() {
		return this.metric.source;
	}

	get sourceType() {
		return this.metric.sourceType;
	}

	get points() {
		const points: DChartPrimaryPoint[] = [];
		this.dataSelect?.outputs?.forEach((output) => {
			const p = this.mmSamplesService.createDChartPointsFromOutput(output);
			points.push(...p);
		});
		return points;
	}

	get primaryPoint(): DChartPrimaryPoint | null {
		const points = this.points;

		return points[0] || null;
	}

	get comparePoint() {
		const primaryPoint = this.primaryPoint;

		return primaryPoint?.comparePoints[0] || null;
	}

	get lastSyncFromData() {
		return this.dataSelect?.dataRoot?.lastSync ?? null;
	}

	get dataErrorStatusObjects() {
		return this.dataSelect?.dataRoot?.statusObjects;
	}

	get showDataStatusIcon(): boolean {
		const lastSync = !!this.lastSyncFromData,
			statusObjectsLength = this.dataErrorStatusObjects?.length || 0;

		return lastSync || statusObjectsLength > 0;
	}

	get showAiChatButton(): boolean {
		return this.aiChatService.isEligibleForAiChat;
	}

	// the mobile drawer shows the related metric only: opening, analyzing or connecting it leaves the drawer
	get isReadOnly(): boolean {
		return this.metricDetailsService.isMobileMode;
	}

	setData(data: { previewData: DSData.RootTop | null; paramHash: string }) {
		if (data.paramHash === this.paramHash) {
			this.dataSelect?.setFakeData(data.previewData);
			this.isLoading = false;
		}
	}

	requestDataQuery(dataSelect: DataSelect, paramHash: string) {
		// if request already in progress, cancel it
		if (this.dataQueryXHR && this.dataQueryXHR.readyState !== 4) {
			this.dataQueryXHR.abort();
		}

		const promise = this.mmDataSelectsService.fetchData(dataSelect, true);

		promise
			.then((previewData: DSData.RootTop) => {
				this.setData({
					previewData: previewData,
					paramHash,
				});
			})
			.catch((errorData: RequestError) => {
				this.setData({
					previewData: null,
					paramHash,
				});
				this.requestService.handleGenericThrownError(errorData);
			});

		this.dataQueryXHR = (promise.jqPromise as JQuery.jqXHR) ?? null;
	}

	private recreateDataQuery() {
		next(() => {
			// Create Chart Block and DS, push to store
			next(() => {
				// DS can be read from store. It depends on forceLoadingState
				const dataSelectData =
					this.args.chartState.block?.blockSetup?.firstShownDataSelect?.toJSON(true); // The chart component validates the date range, just use the DS if it exists here

				if (dataSelectData && this.isConnected) {
					const rangeOnlyFormula = copyObject(this.args.dateRange?.formula)!;
					delete rangeOnlyFormula.granulation;

					const dateRangeId = getDateRangeIdFromFormula(rangeOnlyFormula);

					const newParamHash = utf8_to_b64(
						[JSON.stringify(this.args.model.metricData), dateRangeId].join(''),
					);

					if (this.paramHash !== newParamHash) {
						this.isLoading = true;
						this.dataSelect?.setFakeData(null);

						this.paramHash = newParamHash;

						dataSelectData.id = this.metricDetailsService.generateMetricInsightsUUID();
						dataSelectData.blockId = null;
						const granulation = dataSelectData.dateRange!.granulation!; // a data select always has a granulation
						granulation.isWholeRange = true;
						granulation.timeUnit = null;
						granulation.multiplier = null;
						delete dataSelectData.compares;

						next(() => {
							const dataSelectModel =
									this.mmDataSelectsService.createNewModel(dataSelectData),
								dateRange = dataSelectModel.limitations.dateRange;

							// dimensionValues are ignored which is okay
							dataSelectModel.updateMetricSetupData(
								this.metricSetup.metricSetupWithoutFiltersData,
							);

							if (dateRange && !dateRange.isAllTime && !dateRange.isSince) {
								const newCompare = DsCompare.createFromFormula(null);
								newCompare.updateFromTypeId(DsCompare.customPreviousId, dateRange);
								dataSelectModel.changeCompare(
									dataSelectModel.limitations.compareCMs[0]!,
									newCompare,
								);
							}

							this.requestDataQuery(dataSelectModel, newParamHash);

							this.unloadDataSelect();
							this.dataSelect = dataSelectModel;
						});
					}
				} else {
					this.paramHash = '';
				}
			});
		});
	}

	@action
	refreshDataQuery() {
		this.isLoading = true;
		next(() => {
			next(() => {
				this.isLoading = false; // To coincide with runloops in recreateDataQuery
			});
		});

		once(this, this.recreateDataQuery);
	}

	@action
	viewInsights() {
		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup: this.metricSetup,
				dateRange: this.args.dateRange?.formula,
			},
			this.args.origin,
		);
	}

	@action
	openGenie() {
		this.aiChatService.openGenieForMetricSetup(this.metricSetup);
	}

	@action
	connectSource() {
		const sourceType = this.sourceType;
		if (sourceType) {
			void this.connectService.connectSourceFromIdOrKey(sourceType.key, {
				origin: this.args.origin,
			});
		}
	}

	unloadDataSelect() {
		if (this.dataSelect) {
			this.dataSelect.model.unloadRecord();
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.dataQueryXHR?.abort();
		this.unloadDataSelect();
	}
	<template>
		<div
			class="module-box"
			{{didUpdate this.refreshDataQuery @model @dateRange.formulaId @forceLoadingState}}
		>
			<div class="module-box__title">
				<div class="title-container">
					<div class="source">
						<img
							src={{this.metric.sourceAvatar}}
							width="16"
							height="16"
							alt={{this.metric.sourceName}}
						/>
						<span>
							<ScrollableTextComponent>
								{{this.metric.sourceName}}
							</ScrollableTextComponent>
							<ToolTipComponent @text={{this.metric.sourceName}} />
						</span>
						{{#if this.showDataStatusIcon}}
							<DataStatusIconComponent
								@context="metric"
								@lastSync={{this.lastSyncFromData}}
								@statusObjects={{this.dataErrorStatusObjects}}
								@theme={{this.navigationService.theme.id}}
								@skipActions={{this.isReadOnly}}
							/>
						{{/if}}
					</div>
					<div class="metric-name">
						<ScrollableTextComponent>{{this.metric.name}}</ScrollableTextComponent>
					</div>
				</div>
			</div>

			{{#if this.isConnected}}
				{{#if this.isLoading}}
					<div class="module-box__loader">
						<BarLoaderComponent />
					</div>
				{{else if this.primaryPoint}}
					<div class="module-box__value">
						{{this.primaryPoint.valueStr}}
					</div>

					<div class="compare-action-area">
						{{#if this.comparePoint}}
							<span class="change">
								<span class="indicator {{this.comparePoint.indicatorClass}}">
									{{this.comparePoint.changeStr}}
								</span>
								<span>
									vs previous period
								</span>
							</span>
						{{/if}}

						{{#unless this.isReadOnly}}
							<div class="compare-action-area__actions">
								<SubmitButtonComponent
									@text="View"
									@onTrigger={{this.viewInsights}}
									@noDelay={{true}}
								/>
								{{#if this.showAiChatButton}}
									<CommonGenieButtonComponent @openChat={{this.openGenie}} />
								{{/if}}
							</div>
						{{/unless}}
					</div>
				{{else}}
					<div class="module-box__value no-data">Not available</div>
				{{/if}}
			{{else if this.isReadOnly}}
				<div class="module-box__value no-data">Not available</div>
			{{else if this.sourceType}}
				<div class="module-box__value no-data">
					<SourceConnectButtonComponent
						@sourceType={{this.sourceType}}
						@text="Connect"
						@onConnect={{this.connectSource}}
						@theme="green"
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
